<template>
  <div class="dashboard-container">
    <!-- 頂部導航欄 -->
    <div class="navbar">
      <div class="navbar-left">
        <img :src="logoPath" alt="SOLARSDGS" class="navbar-logo">
        <span class="navbar-title">{{ t('dashboard.title') }}</span>
        <span class="device-badge">{{ t('dashboard.deviceId') }}: {{ deviceId }}</span>
      </div>
      <div class="navbar-right">
        <span class="connection-status" :class="{ connected: isConnected }">
          {{ isConnected ? `🟢 ${t('dashboard.online')}` : `🔴 ${t('dashboard.offline')}` }}
        </span>
        <span class="user-name">👤 {{ userName }}</span>
        <button @click="$router.push('/coating')" class="btn-back">{{ t('coating.navButton') }}</button>
        <button @click="handleBack" class="btn-back">{{ t('nav.devices') }}</button>
        <button @click="handleLogout" class="btn-logout">{{ t('nav.logout') }}</button>
      </div>
    </div>

    <!-- 主內容區域 -->
    <div class="dashboard-content">
      <!-- 載入中 -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ t('common.loading') }}</p>
      </div>

      <!-- 錯誤訊息 -->
      <div v-else-if="error" class="error-state">
        <p class="error-icon">⚠️</p>
        <p class="error-message">{{ error }}</p>
        <button @click="loadDashboard" class="btn-retry">{{ t('common.refresh') }}</button>
      </div>

      <!-- Dashboard 主內容 -->
      <div v-else class="dashboard-main">
        <!-- 設備資訊與狀態 (移到最上方) -->
        <div class="info-section">
          <h2 class="section-title">{{ t('dashboard.deviceInfo') }}</h2>
          <div class="info-grid">
            <div class="info-card">
              <div class="info-label">{{ t('dashboard.deviceName') }}</div>
              <div class="info-value">{{ deviceInfo?.device_name || 'N/A' }}</div>
            </div>
            <div class="info-card">
              <div class="info-label">{{ t('dashboard.lastUpdate') }}</div>
              <div class="info-value">{{ formatDateTime(latestData?.timestamp) }}</div>
            </div>
            <div class="info-card">
              <div class="info-label">{{ t('dashboard.dataInterval') }}</div>
              <div class="info-value">{{ deviceConfig.dataInterval }} {{ t('dashboard.seconds') }}</div>
            </div>
          </div>
        </div>

        <!-- 即時功率卡片 -->
        <div class="power-section">
          <h2 class="section-title">{{ t('dashboard.powerData') }}</h2>
          <div class="power-cards">
            <div class="power-card pg-card">
              <div class="card-header">
                <div class="card-icon">⚡</div>
                <h3>{{ t('dashboard.generation') }} (PG)</h3>
              </div>
              <div class="card-value">{{ formatNumber(latestData?.pg) }}<span class="unit">{{ t('dashboard.unit.watt') }}</span></div>
              <div class="card-footer">Generation Power</div>
            </div>

            <div class="power-card pa-card">
              <div class="card-header">
                <div class="card-icon">🔌</div>
                <h3>{{ t('dashboard.loadA') }} (PA)</h3>
              </div>
              <div class="card-value">{{ formatNumber(latestData?.pa) }}<span class="unit">{{ t('dashboard.unit.watt') }}</span></div>
              <div class="efficiency" :class="getEfficiencyClass(pagEfficiency)">
                {{ formatEfficiency(pagEfficiency) }}
              </div>
            </div>

            <div class="power-card pp-card">
              <div class="card-header">
                <div class="card-icon">💡</div>
                <h3>{{ t('dashboard.loadP') }} (PP)</h3>
              </div>
              <div class="card-value">{{ formatNumber(latestData?.pp) }}<span class="unit">{{ t('dashboard.unit.watt') }}</span></div>
              <div class="efficiency" :class="getEfficiencyClass(ppgEfficiency)">
                {{ formatEfficiency(ppgEfficiency) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Factor 設定 (修正係數) - 僅超級使用者可見 -->
        <div v-if="isSuperUser" class="factor-section">
          <h2 class="section-title">{{ t('dashboard.factorConfig.title') }}</h2>
          <div class="factor-controls">
            <div class="factor-input-group">
              <label for="factor-a">{{ t('dashboard.factorConfig.factorA') }}:</label>
              <input
                id="factor-a"
                v-model.number="factorA"
                type="number"
                step="0.001"
                min="0.5"
                max="2.0"
                class="factor-input"
              />
            </div>
            <div class="factor-input-group">
              <label for="factor-p">{{ t('dashboard.factorConfig.factorP') }}:</label>
              <input
                id="factor-p"
                v-model.number="factorP"
                type="number"
                step="0.001"
                min="0.5"
                max="2.0"
                class="factor-input"
              />
            </div>
            <button @click="updateFactors" class="btn-update-factor" :disabled="updatingFactor">
              {{ updatingFactor ? t('dashboard.factorConfig.updating') : t('dashboard.factorConfig.updateButton') }}
            </button>
            <button @click="resetFactors" class="btn-reset-factor">
              {{ t('dashboard.factorConfig.resetButton') }}
            </button>
          </div>
          <div class="factor-info">
            <p>💡 {{ t('dashboard.factorConfig.info') }}</p>
            <p>{{ t('dashboard.factorConfig.formulaPGA') }}</p>
            <p>{{ t('dashboard.factorConfig.formulaPGP') }}</p>
          </div>
        </div>

        <!-- 功率趨勢圖表 -->
        <div class="chart-section">
          <div class="section-header">
            <h2 class="section-title">{{ t('dashboard.charts.powerTrend') }}</h2>
            <div class="chart-controls">
              <select v-model="chartTimeRange" @change="updateChart" class="time-range-select">
                <option value="1">{{ t('dashboard.charts.timeRangeOptions.1') }}</option>
                <option value="5">{{ t('dashboard.charts.timeRangeOptions.5') }}</option>
                <option value="10">{{ t('dashboard.charts.timeRangeOptions.10') }}</option>
                <option value="30">{{ t('dashboard.charts.timeRangeOptions.30') }}</option>
                <option value="60">{{ t('dashboard.charts.timeRangeOptions.60') }}</option>
                <option value="360">{{ t('dashboard.charts.timeRangeOptions.360') }}</option>
                <option value="1440">{{ t('dashboard.charts.timeRangeOptions.1440') }}</option>
              </select>
              <button @click="openDownloadModal" class="btn-download">
                📥 {{ t('dashboard.charts.downloadButton') }}
              </button>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="chartCanvas"></canvas>
          </div>
        </div>

        <!-- 總發電量 (kWh / 區間): 10 分鐘基礎格加總, 自己的時間選擇 (最小 10 分鐘) -->
        <div v-if="energySeries.length > 0" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">
              {{ t('dashboard.charts.energyTrend') }}
              <span class="vi-meta">
                Σ PG {{ energyTotal.pgKwh.toFixed(3) }} · PA {{ energyTotal.paKwh.toFixed(3) }} · PP {{ energyTotal.ppKwh.toFixed(3) }} kWh
              </span>
            </h2>
            <div class="chart-controls">
              <select v-model="energyInterval" @change="loadEnergy" class="time-range-select">
                <option value="10">{{ t('dashboard.charts.energyIntervalOptions.10') }}</option>
                <option value="30">{{ t('dashboard.charts.energyIntervalOptions.30') }}</option>
                <option value="60">{{ t('dashboard.charts.energyIntervalOptions.60') }}</option>
                <option value="360">{{ t('dashboard.charts.energyIntervalOptions.360') }}</option>
                <option value="1440">{{ t('dashboard.charts.energyIntervalOptions.1440') }}</option>
              </select>
              <button @click="downloadEnergyCsv" class="btn-download">
                📥 {{ t('dashboard.charts.downloadCsv') }}
              </button>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="energyChartCanvas"></canvas>
          </div>
        </div>

        <!-- PGA 效率趨勢圖 (時間與功率圖連動) -->
        <div class="chart-section">
          <div class="section-header">
            <h2 class="section-title">{{ t('dashboard.charts.pagTrend') }}</h2>
          </div>
          <div class="chart-container">
            <canvas ref="pagChartCanvas"></canvas>
          </div>
        </div>

        <!-- PGP 效率趨勢圖 (時間與功率圖連動) -->
        <div class="chart-section">
          <div class="section-header">
            <h2 class="section-title">{{ t('dashboard.charts.pgpTrend') }}</h2>
          </div>
          <div class="chart-container">
            <canvas ref="pgpChartCanvas"></canvas>
          </div>
        </div>

        <!-- 總發電量增益 (kWh 比, 相對 REF): AP / AR 各一張, 時間軸跟總發電量圖的選擇器; 沒接線的通道整張隱藏 -->
        <div v-if="hasGain('apGain') && !hasRearRef" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">
              {{ t('dashboard.charts.gainTrendAp') }}
              <span class="vi-meta">
                {{ t(`dashboard.charts.energyIntervalOptions.${energyInterval}`) }}
                <template v-if="energyTotal.apGain !== null"> · Σ {{ (energyTotal.apGain * 100).toFixed(2) }}%</template>
              </span>
            </h2>
          </div>
          <div class="chart-container">
            <canvas ref="gainApChartCanvas"></canvas>
          </div>
        </div>
        <div v-if="hasGain('arGain')" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">
              {{ t('dashboard.charts.gainTrendAr') }}
              <span class="vi-meta">
                {{ t(`dashboard.charts.energyIntervalOptions.${energyInterval}`) }}
                <template v-if="energyTotal.arGain !== null"> · Σ {{ (energyTotal.arGain * 100).toFixed(2) }}%</template>
              </span>
            </h2>
          </div>
          <div class="chart-container">
            <canvas ref="gainArChartCanvas"></canvas>
          </div>
        </div>

        <!-- 背面參考片 (不會表面汙染): PP/PG 發電量比, 正面 PG 積塵變暗 → 比值上升 -->
        <div v-if="hasRearRef && energySeries.length > 0" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">
              {{ t('dashboard.charts.rearRefTrend') }}
              <span class="vi-meta">
                {{ t(`dashboard.charts.energyIntervalOptions.${energyInterval}`) }}
                <template v-if="energyTotal.pgKwh > 0"> · Σ {{ (energyTotal.ppKwh / energyTotal.pgKwh * 100).toFixed(2) }}%</template>
              </span>
            </h2>
          </div>
          <div class="chart-container">
            <canvas ref="rearRefChartCanvas"></canvas>
          </div>
        </div>

        <!-- 氣象數據區塊 (僅在設備有對應能力時顯示) -->
        <div v-if="hasSolarRadiation || hasRainfall || hasWindPower" class="weather-section">
          <h2 class="section-title">{{ t('dashboard.weather.solarRadiation') }} / {{ t('dashboard.weather.rainfall') }}</h2>
          <div class="weather-cards">
            <SolarRadiationCard
              v-if="hasSolarRadiation"
              :solar-radiation="weatherData.solarRadiation"
            />
            <RainfallCard
              v-if="hasRainfall"
              :rainfall="weatherData.rainfall"
              :rainfall-rate="weatherData.rainfallRate"
            />
            <WindPowerCard
              v-if="hasWindPower"
              :wind-power="weatherData.windPower"
            />
          </div>
        </div>

        <!-- 氣象數據趨勢圖 (僅在設備有對應能力時顯示，時間與功率圖連動) -->
        <div v-if="hasSolarRadiation || hasRainfall || hasWindPower" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">{{ t('dashboard.charts.weatherTrend') }}</h2>
            <div class="chart-controls">
              <button @click="openWeatherDownloadModal" class="btn-download">
                📥 {{ t('dashboard.charts.downloadWeather') }}
              </button>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="weatherChartCanvas"></canvas>
          </div>
        </div>

        <!-- 相機狀態與控制 (Phase 3.2) -->
        <CameraStatusCard
          v-if="hasCamera"
          :device-id="deviceId"
        />

        <!-- 最新圖像 (Phase 3.1) -->
        <LatestImageCard
          v-if="hasCamera"
          :device-id="deviceId"
          :auto-refresh="true"
          :refresh-interval="60"
          :show-roi-calib="isSuperUser"
        />

        <!-- 板溫趨勢 (NPZ 熱影像) -->
        <div v-if="hasCamera && panelTempData.length > 0" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">{{ t('dashboard.charts.panelTempTrend') }}</h2>
            <div class="chart-controls">
              <select v-model="visionHours" @change="loadVisionCharts" class="time-range-select">
                <option value="48">48h</option>
                <option value="168">7d</option>
                <option value="720">30d</option>
                <option value="2160">90d</option>
              </select>
              <button @click="downloadPanelTempCsv" class="btn-download">
                📥 {{ t('dashboard.charts.downloadCsv') }}
              </button>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="panelTempChartCanvas"></canvas>
          </div>
        </div>

        <!-- 虛擬日照計 (影像融合) - 板溫圖下方 -->
        <div v-if="hasCamera && virtualIrradiance" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">
              {{ t('dashboard.charts.virtualIrradianceTrend') }}
              <span v-if="virtualIrradiance.r2 !== null" class="vi-meta">
                R²={{ virtualIrradiance.r2 }} · {{ virtualIrradiance.trainingPairs }}h
              </span>
            </h2>
            <div class="chart-controls">
              <select v-model="visionHours" @change="loadVisionCharts" class="time-range-select">
                <option value="48">48h</option>
                <option value="168">7d</option>
                <option value="720">30d</option>
              </select>
              <button @click="downloadIrradianceCsv" class="btn-download">
                📥 {{ t('dashboard.charts.downloadCsv') }}
              </button>
            </div>
          </div>
          <!-- 校準中只是影像估計線還沒有; Open-Meteo 日照不需校準, 照常畫 -->
          <div v-if="virtualIrradiance.status === 'calibrating'" class="vi-calibrating">
            ⏳ {{ t('dashboard.charts.viCalibrating') }} ({{ virtualIrradiance.trainingPairs }}/20)
          </div>
          <div class="chart-container">
            <canvas ref="virtualIrradianceChartCanvas"></canvas>
          </div>
        </div>

        <!-- 雨量 (影像融合) - 日照圖下方 -->
        <!-- 鏡頭斷線 points 可能為空, 仍顯示 Open-Meteo 雨量 -->
        <div v-if="hasCamera && rainVision && (rainVision.points.length > 0 || rainVision.rain.length > 0)" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">{{ t('dashboard.charts.rainVisionTrend') }}</h2>
            <div class="chart-controls">
              <select v-model="visionHours" @change="loadVisionCharts" class="time-range-select">
                <option value="48">48h</option>
                <option value="168">7d</option>
                <option value="720">30d</option>
              </select>
              <button @click="downloadRainCsv" class="btn-download">
                📥 {{ t('dashboard.charts.downloadCsv') }}
              </button>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="rainVisionChartCanvas"></canvas>
          </div>
        </div>

        <!-- 表面汙染 (影像) - 雨量圖下方 -->
        <div v-if="hasCamera && soilingVision && soilingVision.days.length > 0" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">{{ t('dashboard.charts.soilingVisionTrend') }}</h2>
            <div class="chart-controls">
              <select v-model="soilingVisionHours" @change="loadSoilingVision" class="time-range-select">
                <option value="720">30d</option>
                <option value="2160">90d</option>
              </select>
              <button @click="downloadSoilingCsv" class="btn-download">
                📥 {{ t('dashboard.charts.downloadCsv') }}
              </button>
            </div>
          </div>
          <div class="chart-container">
            <canvas ref="soilingVisionChartCanvas"></canvas>
          </div>
        </div>

        <!-- 電性汙染 (背面參考片 PP 不積塵): 正面/背面比相對雨後基準的損失 %, 對照影像汙染 -->
        <div v-if="hasRearRef && soilingVision?.electrical?.length" class="chart-section">
          <div class="section-header">
            <h2 class="section-title">
              {{ t('dashboard.charts.soilingPowerTrend') }}
              <span class="vi-meta">{{ t('dashboard.charts.soilingPowerMeta') }}</span>
            </h2>
          </div>
          <div class="chart-container">
            <canvas ref="soilingPowerChartCanvas"></canvas>
          </div>
        </div>

        <!-- GPS 設備位置地圖 -->
        <DeviceMapCard
          v-if="hasGps"
          :device-id="deviceId"
          :auto-refresh="true"
          :refresh-interval="300"
        />
      </div>
    </div>

    <!-- CSV 下載模態框 -->
    <div v-if="showDownloadModal" class="modal-overlay" @click="closeDownloadModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>📥 {{ t('dashboard.csvDownload.title') }}</h3>
          <button @click="closeDownloadModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="date-picker-group">
            <label for="start-date">{{ t('dashboard.csvDownload.startDate') }}:</label>
            <input
              id="start-date"
              v-model="downloadStartDate"
              type="datetime-local"
              class="date-input"
            />
          </div>
          <div class="date-picker-group">
            <label for="end-date">{{ t('dashboard.csvDownload.endDate') }}:</label>
            <input
              id="end-date"
              v-model="downloadEndDate"
              type="datetime-local"
              class="date-input"
            />
          </div>
          <div class="modal-hint">
            💡 {{ t('dashboard.csvDownload.hint') }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDownloadModal" class="btn-cancel">{{ t('dashboard.csvDownload.cancel') }}</button>
          <button @click="downloadPowerData" class="btn-confirm" :disabled="downloadingData">
            {{ downloadingData ? t('dashboard.csvDownload.downloading') : t('dashboard.csvDownload.confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 氣象數據 CSV 下載模態框 -->
    <div v-if="showWeatherDownloadModal" class="modal-overlay" @click="closeWeatherDownloadModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>📥 {{ t('dashboard.csvDownload.weatherTitle') }}</h3>
          <button @click="closeWeatherDownloadModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="date-picker-group">
            <label for="weather-start-date">{{ t('dashboard.csvDownload.startDate') }}:</label>
            <input
              id="weather-start-date"
              v-model="weatherDownloadStartDate"
              type="datetime-local"
              class="date-input"
            />
          </div>
          <div class="date-picker-group">
            <label for="weather-end-date">{{ t('dashboard.csvDownload.endDate') }}:</label>
            <input
              id="weather-end-date"
              v-model="weatherDownloadEndDate"
              type="datetime-local"
              class="date-input"
            />
          </div>
          <div class="modal-hint">
            💡 {{ t('dashboard.csvDownload.weatherHint') }}
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeWeatherDownloadModal" class="btn-cancel">{{ t('dashboard.csvDownload.cancel') }}</button>
          <button @click="downloadWeatherData" class="btn-confirm" :disabled="downloadingWeatherData">
            {{ downloadingWeatherData ? t('dashboard.csvDownload.downloading') : t('dashboard.csvDownload.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { io, Socket } from 'socket.io-client'
import {
  Chart,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import type { ChartConfiguration } from 'chart.js'
import 'chartjs-adapter-dayjs-4' // TimeScale 需要日期 adapter (虛擬日照計時間軸)
import LatestImageCard from '@/components/dashboard/LatestImageCard.vue'
import DeviceMapCard from '@/components/dashboard/DeviceMapCard.vue'
import CameraStatusCard from '@/components/dashboard/CameraStatusCard.vue'
import SolarRadiationCard from '@/components/dashboard/SolarRadiationCard.vue'
import RainfallCard from '@/components/dashboard/RainfallCard.vue'
import WindPowerCard from '@/components/dashboard/WindPowerCard.vue'

// 註冊 Chart.js 組件
Chart.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
)

const router = useRouter()
const { t } = useI18n()
const logoPath = ref('/logo.png')

// 設備資料介面
interface PowerData {
  id: number
  deviceId: string
  timestamp: string
  pg: number
  pa: number
  pp: number
  pagEfficiency: number  // 修正: 使用正確的欄位名稱
  ppgEfficiency: number  // ✅ 修正: 與資料庫欄位名稱一致
}

interface DeviceInfo {
  device_id: string
  device_name?: string
  device_type?: string
  status: string
  timezone?: string  // 新增: 設備時區 (例如: Asia/Taipei)
  last_seen?: string
  capabilities?: string[]  // 設備能力: power, solar_radiation, rainfall, wind_power, camera, gps
}

// 狀態管理
const deviceId = ref('')
const userName = ref('')
const loading = ref(false)
const error = ref('')
const isConnected = ref(false)
const isSuperUser = ref(false)  // 是否為超級使用者（可訪問所有設備 '*'）

const latestData = ref<PowerData | null>(null)
const historicalData = ref<PowerData[]>([])  // 功率圖數據
const pagHistoricalData = ref<PowerData[]>([])  // PGA 效率圖數據
const pgpHistoricalData = ref<PowerData[]>([])  // PGP 效率圖數據
const deviceInfo = ref<DeviceInfo | null>(null)
const dataCount = ref(0)

// 設備配置（偵測頻率、網路類型）
const deviceConfig = ref({
  dataInterval: 60,  // 預設 60 秒
  networkType: 'unknown' as 'wifi' | '4g' | 'unknown',
  networkName: ''
})

const chartCanvas = ref<HTMLCanvasElement | null>(null)
// 時間粒度（分鐘/點）
const chartTimeRange = ref('10')  // 預設 10 分鐘/點（功率圖）
const pagChartCanvas = ref<HTMLCanvasElement | null>(null)
const pgpChartCanvas = ref<HTMLCanvasElement | null>(null)
// PGA/PGP 效率圖使用 chartTimeRange 與功率圖連動
let chartInstance: Chart | null = null
let pagChartInstance: Chart | null = null
let pgpChartInstance: Chart | null = null
// 功率/PGA/PGP 三圖共用的真實時間軸: 固定 API 回的 [startTime, endTime], 夜間缺資料留白而非壓縮
// (原本用類別軸, 沒資料的小時直接消失, 跟發電量/板溫等時間軸圖對不上)
const powerXRange = ref<{ min: number; max: number } | null>(null)
const powerTimeAxis = () => ({ ...energyTimeAxis(), ...(powerXRange.value ?? {}) })
let refreshInterval: NodeJS.Timeout | null = null
let socket: Socket | null = null

// 氣象數據趨勢圖 (時間與功率圖連動，使用 chartTimeRange)
const weatherChartCanvas = ref<HTMLCanvasElement | null>(null)
const weatherHistoricalData = ref<any[]>([])  // 氣象歷史數據
let weatherChartInstance: Chart | null = null

// 板溫趨勢圖 (NPZ 熱影像, camera 區塊後)
const panelTempChartCanvas = ref<HTMLCanvasElement | null>(null)
const panelTempData = ref<{ capturedAt: string; panelTemp: number; skyTemp: number | null }[]>([])
// 影像系列三張圖 (板溫/日照/雨量) 共用同一個時間選擇與同一段 x 軸, 才對得上
const visionHours = ref('48')
const panelTempHours = visionHours
// 影像系列圖斷線門檻: 拍攝週期 10 分鐘, 漏 1~2 張不該斷線; 夜間休眠 (~10 小時) 才斷
const VISION_GAP_MS = 2 * 60 * 60 * 1000
const visionXRange = () => ({
  min: Date.now() - parseInt(visionHours.value) * 3_600_000,
  max: Date.now()
})

// 總發電量 (kWh / 區間, 由 power_10min 加總) + 發電 % 趨勢 (共用同一序列)
interface EnergyPoint {
  time: string
  pgKwh: number
  paKwh: number
  ppKwh: number
  realBuckets: number
  filledBuckets: number
  apGain: number | null
  arGain: number | null
}
interface EnergyTotal { pgKwh: number; paKwh: number; ppKwh: number; apGain: number | null; arGain: number | null }
const energyChartCanvas = ref<HTMLCanvasElement | null>(null)
const gainApChartCanvas = ref<HTMLCanvasElement | null>(null)
const gainArChartCanvas = ref<HTMLCanvasElement | null>(null)
const rearRefChartCanvas = ref<HTMLCanvasElement | null>(null)
let rearRefChartInstance: Chart | null = null
const energySeries = ref<EnergyPoint[]>([])
const energyTotal = ref<EnergyTotal>({ pgKwh: 0, paKwh: 0, ppKwh: 0, apGain: null, arGain: null })
const energyInterval = ref('60') // 最小 10 分鐘 (基礎格)
let energyChartInstance: Chart | null = null
let gainApChartInstance: Chart | null = null
let gainArChartInstance: Chart | null = null
const hasGain = (key: 'apGain' | 'arGain') => energySeries.value.some(p => p[key] !== null)
let panelTempChartInstance: Chart | null = null

// 虛擬日照計 (影像融合, 板溫圖下方)
interface VirtualIrradianceData {
  status: string
  trainingPairs: number
  r2: number | null
  points: { capturedAt: string; estimatedGhi: number }[]
  openMeteo: { time: string; ghi: number }[]
}
const virtualIrradianceChartCanvas = ref<HTMLCanvasElement | null>(null)
const virtualIrradiance = ref<VirtualIrradianceData | null>(null)
const virtualIrradianceHours = visionHours
let virtualIrradianceChartInstance: Chart | null = null

// 雨量 (影像融合, 日照圖下方)
interface RainVisionData {
  points: { capturedAt: string; rainScore: number }[]
  rain: { time: string; mm: number }[]
}
const rainVisionChartCanvas = ref<HTMLCanvasElement | null>(null)
const rainVision = ref<RainVisionData | null>(null)
const rainVisionHours = visionHours
let rainVisionChartInstance: Chart | null = null

// 落塵 (影像融合, 雨量圖下方)
interface SoilingVisionData {
  baseline: { ap: number; ar: number; ref: number }
  days: {
    day: string
    apSoil: number | null
    arSoil: number | null
    refSoil: number | null
  }[]
  // 背面參考片設備: 每日正午 PG/PP、PA/PP 與相對雨後基準的發電損失 %
  electrical?: { day: string; pgPp: number | null; paPp: number | null; pgLoss: number | null; paLoss: number | null }[]
}
const soilingVisionChartCanvas = ref<HTMLCanvasElement | null>(null)
const soilingPowerChartCanvas = ref<HTMLCanvasElement | null>(null)
let soilingPowerChartInstance: Chart | null = null
const soilingVision = ref<SoilingVisionData | null>(null)
const soilingVisionHours = ref('720')
let soilingVisionChartInstance: Chart | null = null

// 氣象數據下載
const showWeatherDownloadModal = ref(false)
const weatherDownloadStartDate = ref('')
const weatherDownloadEndDate = ref('')
const downloadingWeatherData = ref(false)

// Factor 設定
const factorA = ref(1.0)
const factorP = ref(1.0)
const updatingFactor = ref(false)

// 下載狀態
const downloadingData = ref(false)
const showDownloadModal = ref(false)
const downloadStartDate = ref('')
const downloadEndDate = ref('')

// 計算效率
const pagEfficiency = computed(() => {
  if (!latestData.value?.pagEfficiency) return 0
  // 將字串轉為數字
  return typeof latestData.value.pagEfficiency === 'string'
    ? parseFloat(latestData.value.pagEfficiency)
    : latestData.value.pagEfficiency
})

const ppgEfficiency = computed(() => {
  if (!latestData.value?.ppgEfficiency) return 0
  // 將字串轉為數字
  return typeof latestData.value.ppgEfficiency === 'string'
    ? parseFloat(latestData.value.ppgEfficiency)
    : latestData.value.ppgEfficiency
})

// 設備時區 (從 API 獲取，預設使用台北時區)
const deviceTimezone = computed(() => {
  return deviceInfo.value?.timezone || 'Asia/Taipei'
})

// 設備能力檢查
const hasSolarRadiation = computed(() => {
  const result = deviceInfo.value?.capabilities?.includes('solar_radiation') ?? false
  console.log('🌞 hasSolarRadiation:', result, 'capabilities:', deviceInfo.value?.capabilities)
  return result
})

const hasRainfall = computed(() => {
  return deviceInfo.value?.capabilities?.includes('rainfall') ?? false
})

const hasWindPower = computed(() => {
  return deviceInfo.value?.capabilities?.includes('wind_power') ?? false
})

const hasCamera = computed(() => {
  return deviceInfo.value?.capabilities?.includes('camera') ?? false
})

// 設備有背面參考片 (capability rear_ref, 如 6000): 顯示 PP/PG 比值圖, PP 當作不會汙染的對照
const hasRearRef = computed(() => deviceInfo.value?.capabilities?.includes('rear_ref') ?? false)

const hasGps = computed(() => {
  return deviceInfo.value?.capabilities?.includes('gps') ?? false
})

// 氣象數據 (從 WebSocket 接收)
const weatherData = ref<{
  solarRadiation?: number | null
  rainfall?: number | null
  rainfallRate?: number | null
  windPower?: number | null
}>({})


// 格式化函數
function formatNumber(value?: number): string {
  if (value === undefined || value === null) return '0'
  return value.toLocaleString('zh-TW')
}

function formatEfficiency(value: number): string {
  const formatted = value.toFixed(2)
  return value >= 0 ? `+${formatted}%` : `${formatted}%`
}

function getEfficiencyClass(value: number): string {
  if (value > 10) return 'efficiency-high'
  if (value > 0) return 'efficiency-positive'
  if (value > -10) return 'efficiency-negative'
  return 'efficiency-low'
}

function getStatusClass(status?: string): string {
  return status === 'online' ? 'status-online' : 'status-offline'
}

function getStatusText(status?: string): string {
  return status === 'online' ? t('dashboard.online') : t('dashboard.offline')
}

function getNetworkClass(networkType: string): string {
  switch (networkType) {
    case 'wifi': return 'network-wifi'
    case '4g': return 'network-4g'
    default: return 'network-unknown'
  }
}

function getNetworkText(networkType: string): string {
  switch (networkType) {
    case 'wifi': return 'WiFi'
    case '4g': return '4G'
    default: return t('dashboard.unknown')
  }
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return 'N/A'
  try {
    const date = new Date(dateStr)
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: deviceTimezone.value  // 使用設備時區
    })
  } catch {
    return 'N/A'
  }
}

// 初始載入 Dashboard（只在首次載入時顯示 loading）
async function loadDashboard() {
  loading.value = true
  error.value = ''

  try {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')
    deviceId.value = localStorage.getItem('selectedDeviceId') || ''

    if (!token || !userStr) {
      router.push('/login')
      return
    }

    const user = JSON.parse(userStr)
    userName.value = user.customerName || user.customerCode

    // 檢查是否為超級使用者（可訪問所有設備 '*'）
    isSuperUser.value = user.devices && user.devices.includes('*')

    // 檢查設備訪問權限 ('*' 表示可以訪問所有設備)
    const hasAccess = user.devices && (user.devices.includes('*') || user.devices.includes(deviceId.value))
    if (!hasAccess) {
      error.value = `${t('errors.forbidden')} (${deviceId.value})`
      setTimeout(() => router.push('/devices'), 2000)
      return
    }

    // 先載入設備資訊和最新數據（不渲染圖表）
    await refreshData(false)
    loadEnergy() // 每日總發電量 (功率圖下方)
    loadPanelTemps() // 板溫趨勢 (有 camera 的設備才有資料, 無資料時區塊自動隱藏)
    loadVirtualIrradiance() // 日照融合 (板溫圖下方)
    loadRainVision() // 雨量融合 (日照圖下方)
    loadSoilingVision() // 表面汙染 (雨量圖下方)
    // 連線燈已在 refreshData 依 DB 狀態設定, 不再無條件變綠
    console.log('Dashboard loaded successfully')

  } catch (err: any) {
    console.error('Dashboard loading error:', err)

    if (err.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      router.push('/login')
    } else if (err.response?.status === 403) {
      error.value = err.response.data.message || t('errors.forbidden')
      setTimeout(() => router.push('/devices'), 2000)
    } else {
      error.value = err.response?.data?.message || t('errors.loadFailed')
    }
    isConnected.value = false
  } finally {
    loading.value = false

    // 等待 DOM 更新後再渲染圖表（Canvas 元素此時才存在）
    if (!error.value) {
      await nextTick()
      console.log('[loadDashboard] Loading 完成，開始渲染圖表...')
      await loadHistoricalData()
    }
  }
}

// 刷新數據（靜默更新，不顯示 loading，避免閃爍）
// loadCharts: 是否載入歷史數據並渲染圖表（首次載入時為 false，等 DOM 更新後再載入）
async function refreshData(loadCharts: boolean = true) {
  try {
    const token = localStorage.getItem('token')
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'

    // 獨立載入設備資訊（即使功率數據失敗也要載入設備資訊）
    try {
      const deviceResponse = await axios.get(
        `${apiUrl}/api/devices/${deviceId.value}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (deviceResponse.data.success) {
        deviceInfo.value = deviceResponse.data.data
        // 開頁立刻用 DB 狀態顯示連線燈 (後端每 30s 掃描/ MQTT 翻轉即推), 不等 WebSocket
        isConnected.value = deviceInfo.value?.status === 'online'
        console.log('📱 Device Info loaded:', deviceInfo.value)
        console.log('📱 Capabilities:', deviceInfo.value?.capabilities)
      }
    } catch (deviceErr: any) {
      console.error('Failed to load device info:', deviceErr.message)
    }

    // 載入最新功率數據（可能為空）
    try {
      const latestResponse = await axios.get(
        `${apiUrl}/api/power-data/${deviceId.value}/latest`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (latestResponse.data.success) {
        latestData.value = latestResponse.data.data
        // 同步氣象數據 (氣象站設備如 6000)
        const d = latestResponse.data.data
        weatherData.value = {
          solarRadiation: d?.solarRadiation ?? null,
          rainfall: d?.rainfall ?? null,
          rainfallRate: d?.rainfallRate ?? null,
          windPower: d?.windPower ?? null
        }
      }
    } catch (powerErr: any) {
      console.warn('No power data available:', powerErr.message)
      // 新設備可能沒有功率數據，這是正常的
    }

    // 只在 loadCharts=true 時載入歷史數據（首次載入時跳過，等 Canvas 存在後再載入）
    if (loadCharts) {
      await loadHistoricalData()
    }

  } catch (err: any) {
    console.error('Data refresh error:', err)
    // 靜默失敗，不影響用戶體驗
    if (err.response?.status === 401) {
      router.push('/login')
    }
  }
}

async function loadHistoricalData() {
  try {
    const token = localStorage.getItem('token')
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const interval = parseInt(chartTimeRange.value)  // 時間粒度（分鐘/點）
    const points = 60  // 固定 60 個資料點

    console.log(`[loadHistoricalData] 載入聚合數據: interval=${interval}分鐘, points=${points}, apiUrl=${apiUrl}`)

    const response = await axios.get(
      `${apiUrl}/api/power-data/device/${deviceId.value}/aggregated`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { interval, points }
      }
    )

    console.log('[loadHistoricalData] API 回應:', response.data)

    if (response.data.success) {
      // 後端返回的 records 包含 bucket_time, avg_pg, avg_pa, avg_pp, avg_pag, avg_pgp, avg_solar_radiation 等
      const records = response.data.data.records
      powerXRange.value = {
        min: new Date(response.data.data.startTime).getTime(),
        max: new Date(response.data.data.endTime).getTime()
      }

      // 轉換為前端需要的格式 (功率數據)
      historicalData.value = records.map((record: any) => ({
        timestamp: record.bucket_time,
        pg: parseFloat(record.avg_pg) || 0,
        pa: parseFloat(record.avg_pa) || 0,
        pp: parseFloat(record.avg_pp) || 0,
        pag_efficiency: parseFloat(record.avg_pag) || 0,
        pgp_efficiency: parseFloat(record.avg_pgp) || 0
      }))

      // 同時儲存氣象數據 (從同一個 API 回應)
      weatherHistoricalData.value = records.map((record: any) => ({
        timestamp: record.bucket_time,
        solarRadiation: parseFloat(record.avg_solar_radiation) || 0,
        rainfall: parseFloat(record.avg_rainfall) || 0,
        rainfallRate: parseFloat(record.avg_rainfall_rate) || 0,
        windPower: parseFloat(record.avg_wind_power) || 0
      }))

      dataCount.value = historicalData.value.length
      console.log(`[loadHistoricalData] 成功載入 ${dataCount.value} 筆聚合數據`, historicalData.value.slice(0, 3))

      // 使用 nextTick 確保 DOM 已更新
      await nextTick()
      console.log('[loadHistoricalData] DOM 已更新，開始渲染圖表...')

      renderChart()
      renderPagChart()
      renderPgpChart()

      // 如果設備有氣象能力，也渲染氣象趨勢圖
      if (hasSolarRadiation.value || hasRainfall.value || hasWindPower.value) {
        renderWeatherChart()
      }
    } else {
      console.error('[loadHistoricalData] API 返回失敗:', response.data)
    }
  } catch (err) {
    console.error('[loadHistoricalData] 載入失敗:', err)
  }
}

function updateChart() {
  loadHistoricalData()
}

// 載入 PGA 效率圖數據 (使用 chartTimeRange 與功率圖連動)
async function loadPagData() {
  try {
    const token = localStorage.getItem('token')
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const interval = parseInt(chartTimeRange.value)  // 使用功率圖的時間範圍
    const points = 60

    const response = await axios.get(
      `${apiUrl}/api/power-data/device/${deviceId.value}/aggregated`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { interval, points }
      }
    )

    if (response.data.success) {
      const records = response.data.data.records
      pagHistoricalData.value = records.map((record: any) => ({
        timestamp: record.bucket_time,
        pg: parseFloat(record.avg_pg) || 0,
        pa: parseFloat(record.avg_pa) || 0,
        pp: parseFloat(record.avg_pp) || 0,
        pag_efficiency: parseFloat(record.avg_pag) || 0,
        pgp_efficiency: parseFloat(record.avg_pgp) || 0
      }))
      console.log(`[loadPagData] 載入 ${pagHistoricalData.value.length} 筆 PGA 數據`)
    }
  } catch (err: any) {
    console.error('Failed to load PGA data:', err)
  }
}

// 載入 PGP 效率圖數據 (使用 chartTimeRange 與功率圖連動)
async function loadPgpData() {
  try {
    const token = localStorage.getItem('token')
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const interval = parseInt(chartTimeRange.value)  // 使用功率圖的時間範圍
    const points = 60

    const response = await axios.get(
      `${apiUrl}/api/power-data/device/${deviceId.value}/aggregated`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { interval, points }
      }
    )

    if (response.data.success) {
      const records = response.data.data.records
      pgpHistoricalData.value = records.map((record: any) => ({
        timestamp: record.bucket_time,
        pg: parseFloat(record.avg_pg) || 0,
        pa: parseFloat(record.avg_pa) || 0,
        pp: parseFloat(record.avg_pp) || 0,
        pag_efficiency: parseFloat(record.avg_pag) || 0,
        pgp_efficiency: parseFloat(record.avg_pgp) || 0
      }))
      console.log(`[loadPgpData] 載入 ${pgpHistoricalData.value.length} 筆 PGP 數據`)
    }
  } catch (err: any) {
    console.error('Failed to load PGP data:', err)
  }
}

function renderChart() {
  console.log('renderChart called', {
    hasCanvas: !!chartCanvas.value,
    dataLength: historicalData.value.length,
    hasChartInstance: !!chartInstance
  })

  if (!chartCanvas.value) {
    console.error('Canvas element not found!')
    return
  }

  if (historicalData.value.length === 0) {
    console.warn('No historical data to render')
    return
  }

  // 真實時間軸: 每點 x = 格起始時間 (與發電量/板溫圖同一種軸), 夜間沒資料就留白
  const series = (pick: (i: { pg: number; pa: number; pp: number }) => number) =>
    historicalData.value.map(item => ({ x: new Date(item.timestamp).getTime(), y: pick(item) }))
  const pgData = series(item => item.pg)
  const paData = series(item => item.pa)
  const ppData = series(item => item.pp)

  console.log('Chart data prepared', { pgData: pgData.slice(0, 3) })

  // 如果圖表已存在，只更新數據（避免閃爍）
  if (chartInstance) {
    console.log('Updating existing chart')
    chartInstance.data.datasets[0].data = pgData
    chartInstance.data.datasets[1].data = paData
    chartInstance.data.datasets[2].data = ppData
    Object.assign(chartInstance.options.scales!.x!, powerTimeAxis())
    chartInstance.update('none')  // 'none' 模式：不使用動畫，立即更新
    return
  }

  // 首次創建圖表
  console.log('Creating new chart')
  const config: ChartConfiguration = {
    type: 'line',
    data: {
      datasets: [
        {
          label: `PG (${t('dashboard.generation')})`,
          data: pgData,
          borderColor: '#FFC107',
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointRadius: 2,
          pointHoverRadius: 6
        },
        {
          label: `PA (${t('dashboard.loadA')})`,
          data: paData,
          borderColor: '#2196F3',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointRadius: 2,
          pointHoverRadius: 6
        },
        {
          label: `PP (${t('dashboard.loadP')})`,
          data: ppData,
          borderColor: '#4CAF50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointRadius: 2,
          pointHoverRadius: 6
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        title: {
          display: false
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#ecf0f1',
            font: {
              size: 14,
              weight: 'bold'
            },
            padding: 15,
            usePointStyle: true
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#FFC107',
          bodyColor: '#ecf0f1',
          borderColor: '#FFC107',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: (context) => {
              return `${context.dataset.label}: ${context.parsed.y} mW`
            }
          }
        }
      },
      scales: {
        x: powerTimeAxis(),
        y: {
          beginAtZero: true,
          ticks: {
            color: '#b0bec5',
            font: {
              size: 12
            },
            callback: (value) => `${value} mW`
          },
          grid: {
            color: 'rgba(176, 190, 197, 0.1)'
          }
        }
      }
    }
  }

  try {
    chartInstance = new Chart(chartCanvas.value, config)
    console.log('Chart created successfully!', chartInstance)
  } catch (error) {
    console.error('Failed to create chart:', error)
  }
}

// PGA 效率趨勢圖渲染（Bar Chart）
async function renderPagChart() {
  if (!pagChartCanvas.value) {
    console.error('PGA canvas element not found!')
    return
  }

  // 載入 PGA 圖表的數據
  await loadPagData()

  if (pagHistoricalData.value.length === 0) {
    console.warn('No historical data for PGA chart')
    return
  }

  // 真實時間軸 (與功率圖同軸): bar 放在格中央
  const halfIv = parseInt(chartTimeRange.value) * 30_000
  const pagData = pagHistoricalData.value.map(item => ({ x: new Date(item.timestamp).getTime() + halfIv, y: item.pag_efficiency || 0 }))

  // 如果圖表已存在，只更新數據（避免閃爍）
  if (pagChartInstance) {
    pagChartInstance.data.datasets[0].data = pagData
    Object.assign(pagChartInstance.options.scales!.x!, powerTimeAxis())
    pagChartInstance.update('none')  // 'none' 模式：不使用動畫，立即更新
    return
  }

  // 首次創建 PGA 圖表（Bar Chart）
  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      datasets: [
        {
          label: `${t('dashboard.pagEfficiency')} (%)`,
          data: pagData,
          backgroundColor: 'rgba(33, 150, 243, 0.7)',
          borderColor: '#2196F3',
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        title: {
          display: false
        },
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#FFC107',
          bodyColor: '#ecf0f1',
          borderColor: '#2196F3',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y
              return `${t('dashboard.pagEfficiency')}: ${value !== null ? value.toFixed(2) : '0.00'}%`
            }
          }
        }
      },
      scales: {
        x: powerTimeAxis(),
        y: {
          beginAtZero: true,
          ticks: {
            color: '#b0bec5',
            font: { size: 12 },
            callback: (value) => `${value}%`
          },
          grid: {
            color: 'rgba(176, 190, 197, 0.1)'
          }
        }
      }
    }
  }

  try {
    pagChartInstance = new Chart(pagChartCanvas.value, config)
    console.log('PGA chart created successfully!')
  } catch (error) {
    console.error('Failed to create PGA chart:', error)
  }
}

// PGP 效率趨勢圖渲染（Bar Chart）
async function renderPgpChart() {
  if (!pgpChartCanvas.value) {
    console.error('PGP canvas element not found!')
    return
  }

  // 載入 PGP 圖表的數據
  await loadPgpData()

  if (pgpHistoricalData.value.length === 0) {
    console.warn('No historical data for PGP chart')
    return
  }

  // 真實時間軸 (與功率圖同軸): bar 放在格中央
  const halfIv = parseInt(chartTimeRange.value) * 30_000
  const pgpData = pgpHistoricalData.value.map(item => ({ x: new Date(item.timestamp).getTime() + halfIv, y: item.pgp_efficiency || 0 }))

  // 如果圖表已存在，只更新數據（避免閃爍）
  if (pgpChartInstance) {
    pgpChartInstance.data.datasets[0].data = pgpData
    Object.assign(pgpChartInstance.options.scales!.x!, powerTimeAxis())
    pgpChartInstance.update('none')  // 'none' 模式：不使用動畫，立即更新
    return
  }

  // 首次創建 PGP 圖表（Bar Chart）
  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      datasets: [
        {
          label: `${t('dashboard.pgpEfficiency')} (%)`,
          data: pgpData,
          backgroundColor: 'rgba(76, 175, 80, 0.7)',
          borderColor: '#4CAF50',
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        title: {
          display: false
        },
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#FFC107',
          bodyColor: '#ecf0f1',
          borderColor: '#4CAF50',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y
              return `${t('dashboard.pgpEfficiency')}: ${value !== null ? value.toFixed(2) : '0.00'}%`
            }
          }
        }
      },
      scales: {
        x: powerTimeAxis(),
        y: {
          beginAtZero: true,
          ticks: {
            color: '#b0bec5',
            font: { size: 12 },
            callback: (value) => `${value}%`
          },
          grid: {
            color: 'rgba(176, 190, 197, 0.1)'
          }
        }
      }
    }
  }

  try {
    pgpChartInstance = new Chart(pgpChartCanvas.value, config)
    console.log('PGP chart created successfully!')
  } catch (error) {
    console.error('Failed to create PGP chart:', error)
  }
}

// 載入 Factor 配置
async function loadFactorConfig() {
  try {
    const token = localStorage.getItem('token')
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'

    const response = await axios.get(
      `${apiUrl}/api/devices/${deviceId.value}/config`,
      { headers: { Authorization: `Bearer ${token}` } }
    )

    if (response.data.success) {
      const config = response.data.data
      factorA.value = config.factor_a || 1.0
      factorP.value = config.factor_p || 1.0
      console.log(`Factor config loaded: A=${factorA.value}, P=${factorP.value}`)
    }
  } catch (err) {
    console.error('Failed to load factor config:', err)
  }
}

// 更新 Factor 配置
async function updateFactors() {
  if (updatingFactor.value) return

  updatingFactor.value = true

  try {
    const token = localStorage.getItem('token')
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'

    const response = await axios.put(
      `${apiUrl}/api/devices/${deviceId.value}/config`,
      {
        factor_a: factorA.value,
        factor_p: factorP.value
      },
      { headers: { Authorization: `Bearer ${token}` } }
    )

    if (response.data.success) {
      alert(`✅ ${t('dashboard.factorConfig.updateSuccess')}\nFactor A: ${factorA.value}\nFactor P: ${factorP.value}`)
      // 重新載入數據以套用新 Factor
      await refreshData()
    }
  } catch (err: any) {
    console.error('Failed to update factors:', err)
    alert(`❌ ${t('dashboard.factorConfig.updateFailed')}: ${err.response?.data?.message || err.message}`)
  } finally {
    updatingFactor.value = false
  }
}

// 重置 Factor 為 1.0
function resetFactors() {
  factorA.value = 1.0
  factorP.value = 1.0
}

// 開啟下載模態框
function openDownloadModal() {
  // 設置預設時間範圍：過去 7 天
  const endTime = new Date()
  const startTime = new Date(endTime.getTime() - 7 * 24 * 60 * 60 * 1000)

  // 轉換為 datetime-local 格式 (YYYY-MM-DDTHH:mm)
  downloadEndDate.value = endTime.toISOString().slice(0, 16)
  downloadStartDate.value = startTime.toISOString().slice(0, 16)

  showDownloadModal.value = true
}

// 關閉下載模態框
function closeDownloadModal() {
  showDownloadModal.value = false
  downloadStartDate.value = ''
  downloadEndDate.value = ''
}

// 下載發電資料 (CSV)
async function downloadPowerData() {
  if (downloadingData.value) return

  // 驗證日期
  if (!downloadStartDate.value || !downloadEndDate.value) {
    alert(t('dashboard.csvDownload.selectDates'))
    return
  }

  const startTime = new Date(downloadStartDate.value)
  const endTime = new Date(downloadEndDate.value)

  if (startTime >= endTime) {
    alert(t('dashboard.csvDownload.invalidRange'))
    return
  }

  // 檢查時間範圍（最多 90 天）
  const daysDiff = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60 * 24)
  if (daysDiff > 90) {
    alert(t('dashboard.csvDownload.rangeTooLong'))
    return
  }

  downloadingData.value = true

  try {
    // 獲取數據
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'
    const response = await axios.get(`${apiUrl}/api/power-data/device/${deviceId.value}/range`, {
      params: {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString()
      }
    })

    const records = response.data.data.records || []

    if (records.length === 0) {
      alert(t('dashboard.csvDownload.noData'))
      return
    }

    // 轉換為 CSV
    const headers = [
      'Timestamp',
      'Device ID',
      'PG (mW)',
      'PA (mW)',
      'PP (mW)',
      'PGA Efficiency (%)',
      'PGP Efficiency (%)'
    ]

    const rows = records.map((item: any) => {
      // 確保數字類型（API 可能返回字串）
      const pagEff = item.pga_efficiency ? parseFloat(String(item.pga_efficiency)) : 0
      const pgpEff = item.pgp_efficiency ? parseFloat(String(item.pgp_efficiency)) : 0

      return [
        new Date(item.timestamp).toLocaleString('zh-TW', { timeZone: deviceTimezone.value }),
        item.device_id,
        item.pg,
        item.pa,
        item.pp,
        pagEff.toFixed(2),
        pgpEff.toFixed(2)
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any[]) => row.join(','))
    ].join('\n')

    // 下載檔案
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const startLabel = startTime.toISOString().split('T')[0]
    const endLabel = endTime.toISOString().split('T')[0]
    link.href = url
    link.download = `${deviceId.value}_power_data_${startLabel}_to_${endLabel}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    console.log(`✅ CSV 下載成功 - ${records.length} 筆資料`)
    alert(`✅ ${t('dashboard.csvDownload.successCount', { count: records.length })}`)

    // 關閉模態框
    closeDownloadModal()
  } catch (err: any) {
    console.error('❌ CSV 下載失敗:', err)
    alert(t('dashboard.csvDownload.failed') + ': ' + (err.message || t('errors.unknownError')))
  } finally {
    downloadingData.value = false
  }
}

// 氣象數據趨勢圖渲染 (使用 chartTimeRange 與功率圖連動，數據已在 loadHistoricalData 中載入)
function renderWeatherChart() {
  if (!weatherChartCanvas.value) {
    console.error('Weather chart canvas not found!')
    return
  }

  // 氣象數據已在 loadHistoricalData 中載入，無需再次呼叫 API
  if (weatherHistoricalData.value.length === 0) {
    console.warn('No weather historical data to render')
    return
  }

  // 使用固定格式的時間標籤 (不隨語系變更)
  const labels = weatherHistoricalData.value.map(item => {
    const date = new Date(item.timestamp)
    const intervalMinutes = parseInt(chartTimeRange.value)  // 使用功率圖的時間範圍
    const totalMinutes = intervalMinutes * 60

    // 固定格式：MM/DD HH:mm 或 HH:mm:ss
    if (totalMinutes >= 1440) {
      // >= 1 天：顯示 MM/DD HH:mm
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${month}/${day} ${hours}:${minutes}`
    } else if (totalMinutes >= 360) {
      // >= 6 小時：顯示 HH:mm
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    } else {
      // < 6 小時：顯示 HH:mm:ss
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    }
  })

  // 根據設備能力決定顯示哪些數據集
  const datasets: any[] = []

  if (hasSolarRadiation.value) {
    datasets.push({
      label: t('dashboard.weather.solarRadiation') + ' (W/m²)',
      data: weatherHistoricalData.value.map(item => item.solarRadiation),
      borderColor: '#FF9800',
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointRadius: 2,
      pointHoverRadius: 6,
      yAxisID: 'y'
    })
  }

  if (hasRainfall.value) {
    datasets.push({
      label: t('dashboard.weather.rainfallRate') + ' (mm/h)',
      data: weatherHistoricalData.value.map(item => item.rainfallRate),
      borderColor: '#03A9F4',
      backgroundColor: 'rgba(3, 169, 244, 0.1)',
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointRadius: 2,
      pointHoverRadius: 6,
      yAxisID: 'y1'
    })
  }

  if (hasWindPower.value) {
    datasets.push({
      label: t('dashboard.weather.windPower') + ' (W)',
      data: weatherHistoricalData.value.map(item => item.windPower),
      borderColor: '#9C27B0',
      backgroundColor: 'rgba(156, 39, 176, 0.1)',
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointRadius: 2,
      pointHoverRadius: 6,
      yAxisID: 'y1'
    })
  }

  if (weatherChartInstance) {
    weatherChartInstance.data.labels = labels
    weatherChartInstance.data.datasets = datasets
    weatherChartInstance.update('none')
    return
  }

  const config: ChartConfiguration = {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#ecf0f1',
            font: { size: 14, weight: 'bold' },
            padding: 15,
            usePointStyle: true
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#FFC107',
          bodyColor: '#ecf0f1',
          borderColor: '#FFC107',
          borderWidth: 1,
          padding: 12
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#b0bec5',
            font: { size: 11 },
            autoSkip: true,
            maxTicksLimit: 10
          },
          grid: { color: 'rgba(176, 190, 197, 0.1)' }
        },
        y: {
          type: 'linear',
          display: hasSolarRadiation.value,
          position: 'left',
          title: {
            display: true,
            text: 'W/m²',
            color: '#FF9800'
          },
          ticks: {
            color: '#FF9800',
            font: { size: 12 }
          },
          grid: { color: 'rgba(176, 190, 197, 0.1)' }
        },
        y1: {
          type: 'linear',
          display: hasRainfall.value || hasWindPower.value,
          position: 'right',
          title: {
            display: true,
            text: hasRainfall.value ? 'mm/h / W' : 'W',
            color: '#03A9F4'
          },
          ticks: {
            color: '#03A9F4',
            font: { size: 12 }
          },
          grid: { drawOnChartArea: false }
        }
      }
    }
  }

  try {
    weatherChartInstance = new Chart(weatherChartCanvas.value, config)
    console.log('Weather chart created successfully!')
  } catch (error) {
    console.error('Failed to create weather chart:', error)
  }
}

// 氣象數據下載模態框
function openWeatherDownloadModal() {
  const endTime = new Date()
  const startTime = new Date(endTime.getTime() - 7 * 24 * 60 * 60 * 1000)
  weatherDownloadEndDate.value = endTime.toISOString().slice(0, 16)
  weatherDownloadStartDate.value = startTime.toISOString().slice(0, 16)
  showWeatherDownloadModal.value = true
}

function closeWeatherDownloadModal() {
  showWeatherDownloadModal.value = false
  weatherDownloadStartDate.value = ''
  weatherDownloadEndDate.value = ''
}

// 載入板溫時序 (NPZ 熱影像抽出, 由 VPS cron 寫入 DB)
// 三張影像系列圖一起重載 (共用 visionHours)
function loadVisionCharts() {
  loadPanelTemps()
  loadVirtualIrradiance()
  loadRainVision()
}

async function loadPanelTemps() {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const response = await axios.get(
      `${apiUrl}/api/analytics/device/${deviceId.value}/panel-temp?hours=${panelTempHours.value}`
    )
    if (response.data.success) {
      panelTempData.value = response.data.data.records
      await nextTick()
      renderPanelTempChart()
    }
  } catch (err) {
    console.error('[loadPanelTemps] 載入失敗:', err)
  }
}

function renderPanelTempChart() {
  if (!panelTempChartCanvas.value || panelTempData.value.length === 0) return

  // 真實時間軸 (與日照/雨量圖對齊); 拍攝中斷 >VISION_GAP_MS 插 null 讓夜間斷開, 不壓縮
  const pts = panelTempData.value
  const buildSeries = (pick: (i: { panelTemp: number; skyTemp: number | null }) => number | null) => {
    const out: { x: number; y: number | null }[] = []
    pts.forEach((item, i) => {
      const ts = new Date(item.capturedAt).getTime()
      if (i > 0) {
        const prev = new Date(pts[i - 1].capturedAt).getTime()
        if (ts - prev > VISION_GAP_MS) out.push({ x: prev + 1, y: null })
      }
      out.push({ x: ts, y: pick(item) })
    })
    return out
  }

  const datasets = [
    {
      label: t('dashboard.charts.panelTemp') + ' (°C)',
      data: buildSeries(item => item.panelTemp),
      borderColor: '#F44336',
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
      borderWidth: 2,
      tension: 0.3,
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 5,
      spanGaps: false
    },
    {
      label: t('dashboard.charts.skyTemp') + ' (°C)',
      data: buildSeries(item => item.skyTemp),
      borderColor: '#03A9F4',
      backgroundColor: 'rgba(3, 169, 244, 0.05)',
      borderWidth: 2,
      tension: 0.3,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 5,
      spanGaps: false
    }
  ]

  if (panelTempChartInstance) {
    panelTempChartInstance.data.datasets = datasets
    Object.assign(panelTempChartInstance.options.scales!.x!, visionXRange())
    panelTempChartInstance.update()
    return
  }

  panelTempChartInstance = new Chart(panelTempChartCanvas.value, {
    type: 'line',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      plugins: {
        legend: { labels: { color: '#ecf0f1' } },
        tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
      },
      scales: {
        x: {
          type: 'time',
          ...visionXRange(), // 三張影像圖固定同一段 x 軸, 不隨資料伸縮
          time: { displayFormats: { hour: 'MM/DD HH:mm', day: 'MM/DD' } },
          ticks: { color: '#b0bec5', maxTicksLimit: 12 },
          grid: { color: 'rgba(176, 190, 197, 0.1)' }
        },
        y: {
          ticks: { color: '#b0bec5' },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: '°C', color: '#b0bec5' }
        }
      }
    }
  })
}

// 載入虛擬日照計 (影像融合估計 + Open-Meteo 對照)
async function loadVirtualIrradiance() {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const response = await axios.get(
      `${apiUrl}/api/analytics/device/${deviceId.value}/virtual-irradiance?hours=${virtualIrradianceHours.value}`
    )
    if (response.data.success) {
      virtualIrradiance.value = response.data.data
      await nextTick()
      renderVirtualIrradianceChart()
    }
  } catch (err) {
    console.error('[loadVirtualIrradiance] 載入失敗:', err)
  }
}

function renderVirtualIrradianceChart() {
  const vi = virtualIrradiance.value
  if (!virtualIrradianceChartCanvas.value || !vi || (vi.points.length === 0 && vi.openMeteo.length === 0)) return

  // 時間軸 (夜間依真實比例呈現); 拍攝中斷 >VISION_GAP_MS 插 null 讓線斷開 (夜間休眠不連線)
  const estimated: { x: number; y: number | null }[] = []
  vi.points.forEach((p, i) => {
    const ts = new Date(p.capturedAt).getTime()
    if (i > 0) {
      const prev = new Date(vi.points[i - 1].capturedAt).getTime()
      if (ts - prev > VISION_GAP_MS) estimated.push({ x: prev + 1, y: null })
    }
    estimated.push({ x: ts, y: p.estimatedGhi })
  })
  // Open-Meteo 全時段 (含夜間 0), 對照鐘形是否對稱
  const openMeteo = vi.openMeteo.map(o => ({ x: new Date(o.time).getTime(), y: o.ghi }))

  const datasets = [
    {
      // 可信主線: Open-Meteo GHI (PR 計算也用它)
      label: 'Open-Meteo (W/m²)',
      data: openMeteo,
      borderColor: '#03A9F4',
      backgroundColor: 'rgba(3, 169, 244, 0.12)',
      borderWidth: 2.5,
      tension: 0.3,
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 5
    },
    {
      // 次要/實驗: 影像估計 (r²~0.85 但 10 分鐘尺度抖動大), 淡色不搶主線
      label: t('dashboard.charts.viEstimated') + ' (W/m²)',
      data: estimated,
      borderColor: 'rgba(255, 152, 0, 0.55)',
      borderWidth: 1,
      tension: 0.3,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 4,
      spanGaps: false
    }
  ]

  if (virtualIrradianceChartInstance) {
    virtualIrradianceChartInstance.data.datasets = datasets
    Object.assign(virtualIrradianceChartInstance.options.scales!.x!, visionXRange())
    virtualIrradianceChartInstance.update()
    return
  }

  virtualIrradianceChartInstance = new Chart(virtualIrradianceChartCanvas.value, {
    type: 'line',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      plugins: {
        legend: { labels: { color: '#ecf0f1' } },
        tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
      },
      scales: {
        x: {
          type: 'time',
          ...visionXRange(), // 三張影像圖固定同一段 x 軸, 不隨資料伸縮
          time: {
            displayFormats: { hour: 'MM/DD HH:mm', day: 'MM/DD' }
          },
          ticks: { color: '#b0bec5', maxTicksLimit: 12 },
          grid: { color: 'rgba(176, 190, 197, 0.1)' }
        },
        y: {
          min: 0,
          ticks: { color: '#b0bec5' },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: 'W/m²', color: '#b0bec5' }
        }
      }
    }
  })
}

// 載入雨量融合 (影像陰雨指數 + Open-Meteo 雨量)
async function loadRainVision() {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const response = await axios.get(
      `${apiUrl}/api/analytics/device/${deviceId.value}/rain-vision?hours=${rainVisionHours.value}`
    )
    if (response.data.success) {
      rainVision.value = response.data.data
      await nextTick()
      renderRainVisionChart()
    }
  } catch (err) {
    console.error('[loadRainVision] 載入失敗:', err)
  }
}

function renderRainVisionChart() {
  const rv = rainVision.value
  if (!rainVisionChartCanvas.value || !rv) return

  // Open-Meteo 逐時雨量 (量) + 現場確認淋濕 (熱影像 thermal_std<2.5, 準確率98%: 這片真的被雨淋)
  const rainBars = rv.rain.map(r => ({ x: new Date(r.time).getTime() + 1_800_000, y: r.mm }))
  const hourly = new Map<number, { wet: number; n: number }>()
  rv.points.forEach(p => {
    const hour = Math.floor(new Date(p.capturedAt).getTime() / 3_600_000) * 3_600_000
    const acc = hourly.get(hour) ?? { wet: 0, n: 0 }
    hourly.set(hour, { wet: acc.wet + p.rainScore, n: acc.n + 1 })
  })
  const wetBars = [...hourly.entries()]
    .map(([hour, a]) => ({ x: hour + 1_800_000, y: Math.round(a.wet / a.n) }))
    .sort((a, b) => a.x - b.x)

  const datasets: any[] = [
    {
      type: 'bar',
      label: t('dashboard.charts.rainScore') + ' (%)',
      data: wetBars,
      backgroundColor: 'rgba(0, 188, 212, 0.55)',
      borderColor: '#00BCD4',
      borderWidth: 1,
      yAxisID: 'y'
    },
    {
      type: 'bar',
      label: t('dashboard.charts.rainMm') + ' (mm/h)',
      data: rainBars,
      backgroundColor: 'rgba(3, 169, 244, 0.7)',
      borderColor: '#03A9F4',
      borderWidth: 1,
      yAxisID: 'y2'
    }
  ]

  if (rainVisionChartInstance) {
    rainVisionChartInstance.data.datasets = datasets
    Object.assign(rainVisionChartInstance.options.scales!.x!, visionXRange())
    rainVisionChartInstance.update()
    return
  }

  rainVisionChartInstance = new Chart(rainVisionChartCanvas.value, {
    type: 'bar',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      plugins: {
        legend: { labels: { color: '#ecf0f1' } },
        tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
      },
      scales: {
        x: {
          type: 'time',
          ...visionXRange(), // 三張影像圖固定同一段 x 軸, 不隨資料伸縮
          time: { displayFormats: { hour: 'MM/DD HH:mm', day: 'MM/DD' } },
          ticks: { color: '#b0bec5', maxTicksLimit: 12 },
          grid: { color: 'rgba(176, 190, 197, 0.1)' }
        },
        y: {
          min: 0,
          max: 100,
          position: 'left',
          ticks: { color: '#00BCD4' },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: `${t('dashboard.charts.rainScore')} %`, color: '#00BCD4' }
        },
        y2: {
          min: 0,
          position: 'right',
          ticks: { color: '#03A9F4' },
          grid: { drawOnChartArea: false },
          title: { display: true, text: 'mm/h', color: '#03A9F4' }
        }
      }
    }
  })
}

// 載入落塵融合 (三板每日正午亮度)
async function loadSoilingVision() {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const response = await axios.get(
      `${apiUrl}/api/analytics/device/${deviceId.value}/soiling-vision?hours=${soilingVisionHours.value}`
    )
    if (response.data.success) {
      soilingVision.value = response.data.data
      await nextTick()
      renderSoilingVisionChart()
      renderSoilingPowerChart()
    }
  } catch (err) {
    console.error('[loadSoilingVision] 載入失敗:', err)
  }
}

// 電性汙染 vs 影像汙染: 實線 = 發電損失 % (左軸), 虛線 = 同一片板的影像汙染指數 (右軸)
function renderSoilingPowerChart() {
  const sv = soilingVision.value
  const el = sv?.electrical ?? []
  if (!soilingPowerChartCanvas.value || !sv || el.length === 0) return
  const labels = [...new Set([...el.map(e => e.day), ...sv.days.map(d => d.day)])].sort()
  const byDay = <T extends { day: string }>(rows: T[]) => new Map(rows.map(r => [r.day, r]))
  const e = byDay(el)
  const v = byDay(sv.days)
  const line = (label: string, color: string, data: (number | null | undefined)[], yAxisID: string, dashed: boolean) => ({
    label, data: data.map(x => x ?? null), yAxisID, borderColor: color, backgroundColor: color,
    borderWidth: 2, borderDash: dashed ? [6, 4] : [], tension: 0.2, pointRadius: 3, spanGaps: true
  })
  const datasets: any[] = [
    line(t('dashboard.charts.lossPgPp'), '#FFC107', labels.map(d => e.get(d)?.pgLoss), 'y', false),
    line(t('dashboard.charts.lossPaPp'), '#2196F3', labels.map(d => e.get(d)?.paLoss), 'y', false),
    line(t('dashboard.charts.refSoil'), 'rgba(255, 193, 7, 0.6)', labels.map(d => v.get(d)?.refSoil), 'y2', true),
    line(t('dashboard.charts.arSoil'), 'rgba(33, 150, 243, 0.6)', labels.map(d => v.get(d)?.arSoil), 'y2', true)
  ]
  if (soilingPowerChartInstance) {
    soilingPowerChartInstance.data.labels = labels.map(d => d.slice(5))
    soilingPowerChartInstance.data.datasets = datasets
    soilingPowerChartInstance.update()
    return
  }
  soilingPowerChartInstance = new Chart(soilingPowerChartCanvas.value, {
    type: 'line',
    data: { labels: labels.map(d => d.slice(5)), datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: { legend: { labels: { color: '#ecf0f1' } }, tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } },
      scales: {
        x: { ticks: { color: '#b0bec5' }, grid: { color: 'rgba(176, 190, 197, 0.1)' } },
        y: {
          min: 0,
          position: 'left',
          ticks: { color: '#b0bec5', callback: (val: any) => `${val}%` },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: t('dashboard.charts.lossAxis'), color: '#b0bec5' }
        },
        y2: {
          min: 0,
          position: 'right',
          ticks: { color: '#b0bec5' },
          grid: { drawOnChartArea: false },
          title: { display: true, text: t('dashboard.charts.soilIndexAxis'), color: '#b0bec5' }
        }
      }
    }
  })
}

function renderSoilingVisionChart() {
  const sv = soilingVision.value
  if (!soilingVisionChartCanvas.value || !sv || sv.days.length === 0) return

  const labels = sv.days.map(d => d.day.slice(5))
  // 每片板汙染程度 = R-B 對自己乾淨基準的偏移 (busbar 恆定背景已抵銷)
  const datasets: any[] = [
    {
      label: t('dashboard.charts.apSoil'), data: sv.days.map(d => d.apSoil),
      borderColor: '#4CAF50', backgroundColor: '#4CAF50', borderWidth: 2, tension: 0.2, pointRadius: 2, spanGaps: true
    },
    {
      label: t('dashboard.charts.arSoil'), data: sv.days.map(d => d.arSoil),
      borderColor: '#2196F3', backgroundColor: '#2196F3', borderWidth: 2, tension: 0.2, pointRadius: 2, spanGaps: true
    },
    {
      label: t('dashboard.charts.refSoil'), data: sv.days.map(d => d.refSoil),
      borderColor: '#FFC107', backgroundColor: '#FFC107', borderWidth: 2, tension: 0.2, pointRadius: 2, spanGaps: true
    }
  ]

  const shown = datasets.filter(ds => ds.data.some((v: number | null) => v !== null))

  if (soilingVisionChartInstance) {
    soilingVisionChartInstance.data.labels = labels
    soilingVisionChartInstance.data.datasets = shown
    soilingVisionChartInstance.update()
    return
  }

  soilingVisionChartInstance = new Chart(soilingVisionChartCanvas.value, {
    type: 'line',
    data: { labels, datasets: shown },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { labels: { color: '#ecf0f1' } },
        tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
      },
      scales: {
        x: {
          ticks: { color: '#b0bec5', maxTicksLimit: 15 },
          grid: { color: 'rgba(176, 190, 197, 0.1)' }
        },
        y: {
          position: 'left',
          min: 0,
          ticks: { color: '#b0bec5' },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: t('dashboard.charts.soilAxis'), color: '#b0bec5' }
        }
      }
    }
  })
}

async function downloadWeatherData() {
  if (downloadingWeatherData.value) return

  if (!weatherDownloadStartDate.value || !weatherDownloadEndDate.value) {
    alert(t('dashboard.csvDownload.selectDates'))
    return
  }

  const startTime = new Date(weatherDownloadStartDate.value)
  const endTime = new Date(weatherDownloadEndDate.value)

  if (startTime >= endTime) {
    alert(t('dashboard.csvDownload.invalidRange'))
    return
  }

  const daysDiff = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60 * 24)
  if (daysDiff > 90) {
    alert(t('dashboard.csvDownload.rangeTooLong'))
    return
  }

  downloadingWeatherData.value = true

  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'
    const response = await axios.get(`${apiUrl}/api/power-data/device/${deviceId.value}/range`, {
      params: {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString()
      }
    })

    const records = response.data.data.records || []

    if (records.length === 0) {
      alert(t('dashboard.csvDownload.noData'))
      return
    }

    const headers = [
      'Timestamp',
      'Device ID',
      'Solar Radiation (W/m²)',
      'Rainfall (mm)',
      'Rainfall Rate (mm/h)',
      'Wind Power (W)'
    ]

    const rows = records.map((item: any) => [
      new Date(item.timestamp).toLocaleString('zh-TW', { timeZone: deviceTimezone.value }),
      item.device_id,
      item.solar_radiation ?? '',
      item.rainfall ?? '',
      item.rainfall_rate ?? '',
      item.wind_power ?? ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any[]) => row.join(','))
    ].join('\n')

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const startLabel = startTime.toISOString().split('T')[0]
    const endLabel = endTime.toISOString().split('T')[0]
    link.href = url
    link.download = `${deviceId.value}_weather_data_${startLabel}_to_${endLabel}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    console.log(`✅ 氣象 CSV 下載成功 - ${records.length} 筆資料`)
    alert(`✅ ${t('dashboard.csvDownload.successCount', { count: records.length })}`)

    closeWeatherDownloadModal()
  } catch (err: any) {
    console.error('❌ 氣象 CSV 下載失敗:', err)
    alert(t('dashboard.csvDownload.failed') + ': ' + (err.message || t('errors.unknownError')))
  } finally {
    downloadingWeatherData.value = false
  }
}

// 通用 CSV 匯出 — 與功率/氣象下載同格式 (BOM + deviceId_tag_起_迄.csv)
function exportCsv(tag: string, headers: string[], rows: (string | number)[][], from: string, to: string) {
  if (rows.length === 0) {
    alert(t('dashboard.csvDownload.noData'))
    return
  }
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${deviceId.value}_${tag}_${from}_to_${to}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const fmtLocal = (iso: string): string =>
  new Date(iso).toLocaleString('zh-TW', { timeZone: deviceTimezone.value })

// 總發電量 + 發電 % 趨勢 (同一支 API, 同一時間軸)
async function loadEnergy() {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'
    const response = await axios.get(
      `${apiUrl}/api/power-data/device/${deviceId.value}/energy-series?interval=${energyInterval.value}&points=60`
    )
    if (response.data.success) {
      energySeries.value = response.data.data.series
      energyTotal.value = response.data.data.total
      await nextTick()
      renderEnergyChart()
      renderGainChart('apGain')
      renderGainChart('arGain')
      renderRearRefChart()
    }
  } catch (err) {
    console.error('[loadEnergy] 載入失敗:', err)
  }
}

const energyTimeAxis = () => ({
  type: 'time' as const,
  time: { displayFormats: { minute: 'MM/DD HH:mm', hour: 'MM/DD HH:mm', day: 'MM/DD' } },
  ticks: { color: '#b0bec5', maxTicksLimit: 12 },
  grid: { color: 'rgba(176, 190, 197, 0.1)' }
})

function renderEnergyChart() {
  if (!energyChartCanvas.value || energySeries.value.length === 0) return
  const ivMs = parseInt(energyInterval.value) * 60_000
  const bar = (label: string, key: 'pgKwh' | 'paKwh' | 'ppKwh', color: string) => ({
    type: 'bar' as const,
    label,
    data: energySeries.value.map(p => ({ x: new Date(p.time).getTime() + ivMs / 2, y: p[key] })),
    backgroundColor: color,
    borderWidth: 0
  })
  const datasets: any[] = [
    bar(t('dashboard.charts.energyPg'), 'pgKwh', 'rgba(255, 193, 7, 0.85)'),
    bar(t('dashboard.charts.energyPa'), 'paKwh', 'rgba(33, 150, 243, 0.85)'),
    bar(t('dashboard.charts.energyPp'), 'ppKwh', 'rgba(76, 175, 80, 0.85)')
  ]
  if (energyChartInstance) {
    energyChartInstance.data.datasets = datasets
    energyChartInstance.update()
    return
  }
  energyChartInstance = new Chart(energyChartCanvas.value, {
    type: 'bar',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { labels: { color: '#ecf0f1' } },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          callbacks: {
            // 尾註該區間內插了幾格 (補值的 kWh 要打折看)
            afterBody: (items: any[]) => {
              const p = energySeries.value[items[0]?.dataIndex]
              return p && p.filledBuckets > 0 ? `${t('dashboard.charts.energyFilled')}: ${p.filledBuckets}` : ''
            }
          }
        }
      },
      scales: {
        x: energyTimeAxis(),
        y: {
          min: 0,
          ticks: { color: '#b0bec5' },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: 'kWh', color: '#b0bec5' }
        }
      }
    }
  })
}

// AP / AR 各一張增益圖 (共用同一序列與時間軸)
function renderGainChart(key: 'apGain' | 'arGain') {
  const canvas = key === 'apGain' ? gainApChartCanvas.value : gainArChartCanvas.value
  if (!canvas || energySeries.value.length === 0 || !hasGain(key)) return
  const ivMs = parseInt(energyInterval.value) * 60_000
  const color = key === 'apGain' ? '#4CAF50' : '#2196F3'
  const datasets: any[] = [{
    type: 'line' as const,
    label: t(key === 'apGain' ? 'dashboard.charts.gainAp' : 'dashboard.charts.gainAr'),
    data: energySeries.value.map(p => ({ x: new Date(p.time).getTime() + ivMs / 2, y: p[key] === null ? null : (p[key] as number) * 100 })),
    borderColor: color,
    backgroundColor: color,
    borderWidth: 2,
    pointRadius: 2,
    tension: 0.2,
    spanGaps: true
  }]
  const existing = key === 'apGain' ? gainApChartInstance : gainArChartInstance
  if (existing) {
    existing.data.datasets = datasets
    existing.update()
    return
  }
  const inst = new Chart(canvas, {
    type: 'line',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { labels: { color: '#ecf0f1' } },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          callbacks: { label: (c: any) => `${c.dataset.label}: ${c.parsed.y === null ? '—' : c.parsed.y.toFixed(2) + '%'}` }
        }
      },
      scales: {
        x: energyTimeAxis(),
        y: {
          ticks: { color: '#b0bec5', callback: (v: any) => `${v}%` },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: t('dashboard.charts.gainAxis'), color: '#b0bec5' }
        }
      }
    }
  })
  if (key === 'apGain') gainApChartInstance = inst
  else gainArChartInstance = inst
}

// PP/PG 比值 (%): 只算 PG ≥ 視窗最大區間 15% 的區間 (同增益門檻), 清晨黃昏量化噪音不畫
function renderRearRefChart() {
  if (!rearRefChartCanvas.value || energySeries.value.length === 0) return
  const ivMs = parseInt(energyInterval.value) * 60_000
  const maxPg = Math.max(...energySeries.value.map(p => p.pgKwh))
  const datasets: any[] = [{
    type: 'line' as const,
    label: t('dashboard.charts.rearRefRatio'),
    data: energySeries.value.map(p => ({
      x: new Date(p.time).getTime() + ivMs / 2,
      y: p.pgKwh > 0 && p.pgKwh >= maxPg * 0.15 ? (p.ppKwh / p.pgKwh) * 100 : null
    })),
    borderColor: '#AB47BC',
    backgroundColor: '#AB47BC',
    borderWidth: 2,
    pointRadius: 2,
    tension: 0.2,
    spanGaps: true
  }]
  if (rearRefChartInstance) {
    rearRefChartInstance.data.datasets = datasets
    rearRefChartInstance.update()
    return
  }
  rearRefChartInstance = new Chart(rearRefChartCanvas.value, {
    type: 'line',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { labels: { color: '#ecf0f1' } },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          callbacks: { label: (c: any) => `${c.dataset.label}: ${c.parsed.y === null ? '—' : c.parsed.y.toFixed(2) + '%'}` }
        }
      },
      scales: {
        x: energyTimeAxis(),
        y: {
          ticks: { color: '#b0bec5', callback: (v: any) => `${v}%` },
          grid: { color: 'rgba(176, 190, 197, 0.1)' },
          title: { display: true, text: t('dashboard.charts.rearRefAxis'), color: '#b0bec5' }
        }
      }
    }
  })
}

function downloadEnergyCsv() {
  const pct = (g: number | null) => (g === null ? '' : (g * 100).toFixed(2))
  const rows = energySeries.value.map(p => [
    fmtLocal(p.time), p.pgKwh, p.paKwh, p.ppKwh, pct(p.apGain), pct(p.arGain), p.filledBuckets
  ])
  exportCsv(`energy-${energyInterval.value}min`,
    ['Timestamp', 'PG (kWh)', 'PA (kWh)', 'PP (kWh)', 'AP gain vs REF (%)', 'AR gain vs REF (%)', 'Interpolated buckets'],
    rows, energySeries.value[0]?.time.slice(0, 10) ?? '', energySeries.value[energySeries.value.length - 1]?.time.slice(0, 10) ?? '')
}

// 板溫 raw data: 逐幀板溫/天空溫
function downloadPanelTempCsv() {
  const rows = panelTempData.value.map(p => [fmtLocal(p.capturedAt), p.panelTemp ?? '', p.skyTemp ?? ''])
  exportCsv('panel-temp', ['Timestamp', 'Panel Temp (C)', 'Sky Temp (C)'], rows,
    panelTempData.value[0]?.capturedAt.slice(0, 10) ?? '',
    panelTempData.value[panelTempData.value.length - 1]?.capturedAt.slice(0, 10) ?? '')
}

// 日照 raw data: Open-Meteo 逐時 + 影像估計逐幀, 依時間合併 (無值留空)
function downloadIrradianceCsv() {
  const vi = virtualIrradiance.value
  const m = new Map<string, { om?: number; est?: number }>()
  vi?.openMeteo.forEach(o => m.set(o.time, { ...m.get(o.time), om: o.ghi }))
  vi?.points.forEach(p => m.set(p.capturedAt, { ...m.get(p.capturedAt), est: p.estimatedGhi }))
  const entries = [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  const rows = entries.map(([ts, v]) => [fmtLocal(ts), v.om ?? '', v.est ?? ''])
  exportCsv('irradiance', ['Timestamp', 'OpenMeteo GHI (W/m2)', 'Image Estimated GHI (W/m2)'], rows,
    entries[0]?.[0].slice(0, 10) ?? '', entries[entries.length - 1]?.[0].slice(0, 10) ?? '')
}

// 雨量 raw data: Open-Meteo 逐時雨量 + 現場確認淋濕逐幀
function downloadRainCsv() {
  const rv = rainVision.value
  const m = new Map<string, { mm?: number; wet?: number }>()
  rv?.rain.forEach(r => m.set(r.time, { ...m.get(r.time), mm: r.mm }))
  rv?.points.forEach(p => m.set(p.capturedAt, { ...m.get(p.capturedAt), wet: p.rainScore }))
  const entries = [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  const rows = entries.map(([ts, v]) => [fmtLocal(ts), v.mm ?? '', v.wet ?? ''])
  exportCsv('rainfall', ['Timestamp', 'OpenMeteo Rain (mm/h)', 'Onsite Wet Confirmed (%)'], rows,
    entries[0]?.[0].slice(0, 10) ?? '', entries[entries.length - 1]?.[0].slice(0, 10) ?? '')
}

// 污染 raw data: 每日 AP/AR/REF 汙染指數
function downloadSoilingCsv() {
  const days = soilingVision.value?.days ?? []
  const rows = days.map(d => [d.day, d.apSoil ?? '', d.arSoil ?? '', d.refSoil ?? ''])
  exportCsv('soiling', ['Date', 'AP Soiling', 'AR Soiling', 'REF Soiling'], rows,
    days[0]?.day ?? '', days[days.length - 1]?.day ?? '')
}

function handleBack() {
  router.push('/devices')
}

function handleLogout() {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('selectedDeviceId')
  router.push('/login')
}

// WebSocket 連接和事件監聽
function connectWebSocket() {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000'

  socket = io(apiUrl, {
    transports: ['websocket', 'polling'],
    timeout: 10000
  })

  socket.on('connect', () => {
    console.log('✅ WebSocket connected:', socket?.id)

    // 加入設備房間
    if (deviceId.value) {
      socket?.emit('join_device', deviceId.value)
      console.log(`📡 Joined device room: ${deviceId.value}`)
    }
  })

  socket.on('disconnect', (reason) => {
    console.log('❌ WebSocket disconnected:', reason)
  })

  // 監聽相機狀態事件（包含網路類型資訊）
  socket.on('camera_status', (status: any) => {
    console.log('📹 Received camera_status:', status)

    // 更新設備配置
    if (status.networkType) {
      deviceConfig.value.networkType = status.networkType
    }
    if (status.networkName) {
      deviceConfig.value.networkName = status.networkName
    }
    if (status.dataInterval) {
      deviceConfig.value.dataInterval = status.dataInterval
    }
  })

  // 監聯即時功率數據
  socket.on('realtime_data', (data: any) => {
    console.log('⚡ Received realtime_data:', data)

    // 更新最新數據
    if (data.device_id === deviceId.value) {
      latestData.value = {
        id: 0,
        deviceId: data.device_id,
        timestamp: data.timestamp,
        pg: data.pg,
        pa: data.pa,
        pp: data.pp,
        pagEfficiency: data.pag,
        ppgEfficiency: data.ppg  // ✅ 修復：與資料庫欄位名稱一致
      }

      // 更新連線狀態
      isConnected.value = data.online !== false

      // 更新偵測頻率（從 Pico W 發送的 interval 欄位）
      if (data.interval) {
        deviceConfig.value.dataInterval = data.interval
      }
      // 更新網路資訊
      if (data.network) {
        deviceConfig.value.networkName = data.network
        deviceConfig.value.networkType = data.using_4g ? '4g' : 'wifi'
      }

      // 更新氣象數據
      weatherData.value = {
        solarRadiation: data.solarRadiation ?? null,
        rainfall: data.rainfall ?? null,
        rainfallRate: data.rainfallRate ?? null,
        windPower: data.windPower ?? null
      }
    }
  })

  // 監聽設備狀態變更
  socket.on('device_status', (status: any) => {
    console.log('📶 Received device_status:', status)

    // ✅ 修復：後端發送 device_id (snake_case)
    if (status.device_id === deviceId.value) {
      isConnected.value = status.online
    }
  })

  socket.on('error', (error: any) => {
    console.error('WebSocket error:', error)
  })
}

function disconnectWebSocket() {
  if (socket) {
    if (deviceId.value) {
      socket.emit('leave_device', deviceId.value)
    }
    socket.disconnect()
    socket = null
    console.log('WebSocket disconnected')
  }
}

onMounted(() => {
  loadDashboard()
  loadFactorConfig()  // 新增: 載入 Factor 配置

  // 連接 WebSocket
  connectWebSocket()

  // ❌ 禁用 API 輪詢：WebSocket 已提供即時數據，API 輪詢會覆蓋 WebSocket 數據導致歸0
  // refreshInterval = setInterval(() => {
  //   refreshData()
  // }, 5000)
})

onUnmounted(() => {
  // 清理 WebSocket 連接
  disconnectWebSocket()

  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
  if (chartInstance) {
    chartInstance.destroy()
  }
  if (energyChartInstance) {
    energyChartInstance.destroy()
  }
  if (gainApChartInstance) {
    gainApChartInstance.destroy()
  }
  if (gainArChartInstance) {
    gainArChartInstance.destroy()
  }
  if (pagChartInstance) {
    pagChartInstance.destroy()
  }
  if (pgpChartInstance) {
    pgpChartInstance.destroy()
  }
  if (weatherChartInstance) {
    weatherChartInstance.destroy()
  }
  if (panelTempChartInstance) {
    panelTempChartInstance.destroy()
  }
  if (virtualIrradianceChartInstance) {
    virtualIrradianceChartInstance.destroy()
  }
  if (rainVisionChartInstance) {
    rainVisionChartInstance.destroy()
  }
  if (soilingVisionChartInstance) {
    soilingVisionChartInstance.destroy()
  }
})
</script>

<style scoped>
/* ========================================
   Dashboard Container
   ======================================== */
.dashboard-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #3e5563 0%, #2c3e50 100%);
  display: flex;
  flex-direction: column;
}

/* ========================================
   Navbar
   ======================================== */
.navbar {
  background: #2c3e50;
  padding: 15px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-bottom: 3px solid #FFC107;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.navbar-logo {
  height: 40px;
  width: auto;
  border-radius: 8px; /* 圓角設計 */
  background-color: transparent;
}

.navbar-title {
  font-size: 24px;
  font-weight: bold;
  color: #FFC107;
  letter-spacing: 1px;
}

.device-badge {
  background: rgba(255, 193, 7, 0.2);
  color: #FFC107;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #FFC107;
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.connection-status {
  font-size: 14px;
  color: #e74c3c;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 15px;
  background: rgba(231, 76, 60, 0.1);
  transition: all 0.3s;
}

.connection-status.connected {
  color: #2ecc71;
  background: rgba(46, 204, 113, 0.1);
}

.user-name {
  color: #ecf0f1;
  font-size: 14px;
  font-weight: 500;
}

.btn-back,
.btn-logout {
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-back {
  background: #3498db;
  color: white;
}

.btn-back:hover {
  background: #2980b9;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(52, 152, 219, 0.3);
}

.btn-logout {
  background: #e74c3c;
  color: white;
}

.btn-logout:hover {
  background: #c0392b;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(231, 76, 60, 0.3);
}

/* ========================================
   Dashboard Content
   ======================================== */
.dashboard-content {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
}

/* ========================================
   Loading State
   ======================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #ecf0f1;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 6px solid rgba(255, 193, 7, 0.2);
  border-top-color: #FFC107;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-state p {
  font-size: 18px;
  font-weight: 500;
}

/* ========================================
   Error State
   ======================================== */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #ecf0f1;
}

.error-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.error-message {
  font-size: 18px;
  margin-bottom: 30px;
  color: #e74c3c;
  font-weight: 600;
}

.btn-retry {
  padding: 12px 30px;
  background: #FFC107;
  color: #2c3e50;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-retry:hover {
  background: #ffb300;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(255, 193, 7, 0.4);
}

/* ========================================
   Dashboard Main
   ======================================== */
.dashboard-main {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* ========================================
   Section Titles
   ======================================== */
.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #FFC107;
  margin-bottom: 20px;
  border-left: 4px solid #FFC107;
  padding-left: 15px;
}

/* ========================================
   Power Section
   ======================================== */
.power-section {
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.power-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
}

.power-card {
  background: rgba(255, 255, 255, 0.08);
  padding: 25px;
  border-radius: 10px;
  border: 2px solid transparent;
  transition: all 0.3s;
  cursor: pointer;
}

.power-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.pg-card {
  border-color: #FFC107;
}

.pg-card:hover {
  background: rgba(255, 193, 7, 0.1);
  box-shadow: 0 10px 25px rgba(255, 193, 7, 0.3);
}

.pa-card {
  border-color: #2196F3;
}

.pa-card:hover {
  background: rgba(33, 150, 243, 0.1);
  box-shadow: 0 10px 25px rgba(33, 150, 243, 0.3);
}

.pp-card {
  border-color: #4CAF50;
}

.pp-card:hover {
  background: rgba(76, 175, 80, 0.1);
  box-shadow: 0 10px 25px rgba(76, 175, 80, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.card-icon {
  font-size: 32px;
}

.card-header h3 {
  font-size: 16px;
  color: #b0bec5;
  font-weight: 600;
  margin: 0;
}

.card-value {
  font-size: 48px;
  font-weight: bold;
  color: #ecf0f1;
  margin-bottom: 10px;
}

.card-value .unit {
  font-size: 24px;
  color: #b0bec5;
  margin-left: 8px;
  font-weight: 500;
}

.card-footer {
  font-size: 13px;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.efficiency {
  font-size: 16px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 6px;
  text-align: center;
  margin-top: 10px;
}

.efficiency-high {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.efficiency-positive {
  background: rgba(139, 195, 74, 0.2);
  color: #8BC34A;
}

.efficiency-negative {
  background: rgba(255, 152, 0, 0.2);
  color: #FF9800;
}

.efficiency-low {
  background: rgba(244, 67, 54, 0.2);
  color: #F44336;
}

/* ========================================
   Weather Section
   ======================================== */
.weather-section {
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.weather-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin-top: 15px;
}

/* ========================================
   Chart Section
   ======================================== */
.chart-section {
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.chart-controls {
  display: flex;
  gap: 15px;
  align-items: center;
}

.time-range-select {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  color: #ecf0f1;
  border: 2px solid #FFC107;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.time-range-select:hover {
  background: rgba(255, 193, 7, 0.2);
}

.time-range-select:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.3);
}

.time-range-select option {
  background: #263238;
  color: #ecf0f1;
  padding: 8px;
  font-size: 14px;
}

.btn-download {
  padding: 8px 16px;
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
}

.btn-download:hover:not(:disabled) {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(33, 150, 243, 0.4);
}

.btn-download:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.chart-container {
  height: 400px;
  position: relative;
  background: rgba(0, 0, 0, 0.2);
  padding: 20px;
  border-radius: 8px;
}

/* ========================================
   Info Section
   ======================================== */
.info-section {
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-card {
  background: rgba(255, 255, 255, 0.08);
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #FFC107;
  transition: all 0.3s;
}

.info-card:hover {
  background: rgba(255, 255, 255, 0.12);
  transform: translateX(5px);
}

.info-label {
  font-size: 13px;
  color: #b0bec5;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  font-weight: 600;
}

.info-value {
  font-size: 18px;
  color: #ecf0f1;
  font-weight: 700;
}

.status-online {
  color: #2ecc71;
}

.status-offline {
  color: #e74c3c;
}

.network-wifi {
  color: #2ecc71;
}

.network-4g {
  color: #3498db;
}

.network-unknown {
  color: #95a5a6;
}

.network-name {
  font-size: 12px;
  color: #bdc3c7;
}

/* ========================================
   Responsive Design
   ======================================== */
@media (max-width: 1024px) {
  .navbar {
    flex-direction: column;
    gap: 15px;
  }

  .navbar-left,
  .navbar-right {
    width: 100%;
    justify-content: space-between;
  }

  .power-cards {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    padding: 20px 15px;
  }

  .navbar {
    padding: 15px;
  }

  .navbar-title {
    font-size: 18px;
  }

  .section-title {
    font-size: 18px;
  }

  .card-value {
    font-size: 36px;
  }

  .chart-container {
    height: 300px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .navbar-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .navbar-right {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .card-value {
    font-size: 28px;
  }
}

/* ========================================
   Factor Section
   ======================================== */
.factor-section {
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.factor-controls {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 15px;
}

.factor-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.factor-input-group label {
  color: #ecf0f1;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.factor-input {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #ecf0f1;
  border: 2px solid #FFC107;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  width: 100px;
  text-align: center;
  transition: all 0.3s;
}

.factor-input:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.3);
  background: rgba(255, 255, 255, 0.15);
}

.btn-update-factor,
.btn-reset-factor {
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-update-factor {
  background: #4CAF50;
  color: white;
}

.btn-update-factor:hover:not(:disabled) {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
}

.btn-update-factor:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-reset-factor {
  background: #9E9E9E;
  color: white;
}

.btn-reset-factor:hover {
  background: #757575;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(158, 158, 158, 0.3);
}

.factor-info {
  margin-top: 15px;
  padding: 15px;
  background: rgba(255, 193, 7, 0.1);
  border-left: 4px solid #FFC107;
  border-radius: 6px;
}

.factor-info p {
  margin: 5px 0;
  color: #ecf0f1;
  font-size: 13px;
  line-height: 1.6;
}

/* Modal 樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: #2c3e50;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 2px solid #34495e;
}

.modal-header h3 {
  margin: 0;
  color: #ecf0f1;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  color: #ecf0f1;
  font-size: 32px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  line-height: 32px;
  transition: color 0.3s;
}

.modal-close:hover {
  color: #e74c3c;
}

.modal-body {
  padding: 24px;
}

.date-picker-group {
  margin-bottom: 20px;
}

.date-picker-group label {
  display: block;
  margin-bottom: 8px;
  color: #ecf0f1;
  font-size: 14px;
  font-weight: 600;
}

.date-input {
  width: 100%;
  padding: 12px;
  background: #34495e;
  border: 2px solid #4a5f7f;
  border-radius: 6px;
  color: #ecf0f1;
  font-size: 14px;
  transition: border-color 0.3s;
}

.date-input:focus {
  outline: none;
  border-color: #3498db;
}

.modal-hint {
  padding: 12px;
  background: rgba(52, 152, 219, 0.1);
  border-left: 4px solid #3498db;
  border-radius: 4px;
  color: #b0bec5;
  font-size: 13px;
  margin-top: 16px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 2px solid #34495e;
}

.btn-cancel, .btn-confirm {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-cancel {
  background: #95a5a6;
  color: white;
}

.btn-cancel:hover {
  background: #7f8c8d;
}

.btn-confirm {
  background: #3498db;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #2980b9;
}

.btn-confirm:disabled {
  background: #7f8c8d;
  cursor: not-allowed;
  opacity: 0.6;
}
.vi-meta {
  margin-left: 10px;
  font-size: 12px;
  font-weight: normal;
  color: #b0bec5;
}

.vi-calibrating {
  padding: 0 0 8px;
  font-size: 13px;
  color: #b0bec5;
}
</style>
